import { Toaster } from "@/components/ui/sonner";
import { useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useParams,
} from "react-router-dom";
import { identifyUser } from "./features/analytics/posthog";
import { useUser } from "./features/auth/hooks/use-user";
import { useLanguage } from "./features/utils/i18n";
import { useTheme } from "./features/utils/theme";
import { useIsMobile } from "./features/utils/use-mobile";
import { useTitle } from "./features/utils/use-title";
import { WebSocketGateway } from "./features/websockets/provider";
import { PreferencesPage } from "./views/account/preferences";
import { ProfilePage } from "./views/account/security";
import { AgentsPage } from "./views/backoffice/agents";
import { ViewAgentPage } from "./views/backoffice/agents/view";
import { EditAgentPage } from "./views/backoffice/agents/edit";
import { CalendarPage } from "./views/backoffice/calendar";
import { CallsPage } from "./views/backoffice/calls";
import { CompanySettingsPage } from "./views/backoffice/company-settings";
import { ContactsPage } from "./views/backoffice/customers";
import { ContactsEditPage } from "./views/backoffice/customers/edit";
import { ContactsViewPage } from "./views/backoffice/customers/view";
import { DocumentsPage } from "./views/backoffice/documents";
import { AskRepositoryPage } from "./views/backoffice/documents/ask-repository";
import { MetadataConfigPage } from "./views/backoffice/documents/metadata-config";
import { AccountsListPage } from "./views/backoffice/external-accounts";
import { FaqPage } from "./views/backoffice/faq";
import { MessagesPage } from "./views/backoffice/messages";
import { OAuthCallbackPage } from "./views/backoffice/external-accounts/oauth-callback";
import { BackofficeLayout } from "./views/backoffice/layout";
import { MembersPage } from "./views/backoffice/members";
import { UsersPage } from "./views/backoffice/users";
import { NotificationsSettingsPage } from "./views/backoffice/notifications";
import { OnboardingPage } from "./views/onboarding";
import { AdminHomePage } from "./views/backoffice/dashboard";
import { SchedulesListPage } from "./views/backoffice/schedules";
import { ServicesListPage } from "./views/backoffice/services";
import { QualificationFormsPage } from "./views/backoffice/qualification-forms";
import { CreateQualificationFormPage } from "./views/backoffice/qualification-forms/create";
import { EditQualificationFormPage } from "./views/backoffice/qualification-forms/edit";
import { QualificationsPage } from "./views/backoffice/qualifications";
import { CompaniesPage } from "./views/companies";
import { HomeRedirect } from "./views/home-redirect";
import { InvitationPage } from "./views/invitations";
import { Login } from "./views/login";
import { LogoutPage } from "./views/login/logout";
import { SignupPage } from "./views/signup";
import { NoCompanyLanding } from "./views/start";
import { PublicChat } from "./chat";
import { ApiPage } from "./views/backoffice/api";
import { ConnectorsPage } from "./views/backoffice/connectors";
import { NumberRequestPage } from "./views/backoffice/connectors/number-request";
import { ConnectorsPersonalizationPage } from "./views/backoffice/connectors/personalization";
import { DevicesPage } from "./views/backoffice/devices";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { ToolsPage } from "./views/backoffice/tools";
import { ConfigureToolPage } from "./views/backoffice/tools/configure";
import { AgentToolRedirect } from "./views/backoffice/tools/agent-tool-redirect";
import { ToolsLayout } from "./views/backoffice/tools/tools-layout";
import { AdminDashboardPage } from "./views/admin/analytics/dashboard";
import { AdminControlCenterPage } from "./views/admin/control-center";
import { AdminClientsPage } from "./views/admin/clients";
import { AdminClientPage } from "./views/admin/clients/detail";
import {
  AdminClientUsersTab,
  ClientChannelsTab,
} from "./views/admin/clients/tabs/channels";
import { ClientOverviewTab } from "./views/admin/clients/tabs/overview";
import { DevicesTable } from "./views/backoffice/devices/components/devices-table";
import { DeviceBasesTable } from "./views/backoffice/devices/components/bases-table";
import { AdminTechnicalPage } from "./views/admin/analytics/technical";
import { AdminCostsPage } from "./views/admin/analytics/costs";
import { AdminSessionsPage } from "./views/admin/analytics/sessions";
import { AdminStabilityPage } from "./views/admin/analytics/stability";
import { AdminExternalCallsPage } from "./views/admin/analytics/external-calls";
import { AdminContextPressurePage } from "./views/admin/analytics/context-pressure";
import { AdminMediaHealthPage } from "./views/admin/analytics/media-health";
import { AdminCreditsPage } from "./views/admin/analytics/credits";
import { AdminToolErrorsPage } from "./views/admin/tool-errors";
import { AdminSyntheticCallsPage } from "./views/admin/analytics/synthetic-calls";
import { AdminNumberRequestsPage } from "./views/admin/number-requests";
import { AdminNumberRequestPage } from "./views/admin/number-requests/detail";
import { AdminDemoSpacesPage } from "./views/admin/demo-spaces";
import { AdminProvisioningPage } from "./views/admin/provisioning";
import { AdminTelephonyPage } from "./views/admin/telephony";
import { AdminLayout, AdminOnly } from "./views/admin/layout";
import { ClientDashboardTab } from "./views/admin/clients/tabs/dashboard";
import { ClientCallsTab } from "./views/admin/clients/tabs/calls";
import {
  backOfficeAccess,
  type BackOfficeAccess,
} from "./features/auth/back-office-access";
import { AdminExplorerIndexPage } from "./views/admin/explorer";
import { AdminEntityTablePage } from "./views/admin/explorer/table";
import { AdminEntityDetailPage } from "./views/admin/explorer/detail";
import { ClientDataTab } from "./views/admin/clients/tabs/data";
import { RouteErrorPage } from "./views/route-error";

/**
 * `/:company/admin/x` → `/admin/x`.
 *
 * The back-office was a section of the tenant application before it became its
 * own; the tenant id in those URLs never meant anything to it (every page is
 * cross-tenant), so the redirect just drops it and keeps the rest, query string
 * included.
 */
function LegacyAdminRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/[^/]+\/admin/, "");
  const target = rest === "" || rest === "/" ? "/live" : rest;
  return <Navigate to={`/admin${target}${location.search}`} replace />;
}

/** `/admin/tenants/:id` → the client's file, which is where that page went. */
function LegacyTenantRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  return <Navigate to={`/admin/clients/${clientId}`} replace />;
}

const routerGen = (backOffice: BackOfficeAccess | null) =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        element={
          <div className="bg-canvas min-h-dvh">
            <Outlet />
          </div>
        }
        // Every route below falls back on this one: a render error anywhere
        // gets a page in the app's style instead of React Router's own.
        errorElement={<RouteErrorPage />}
      >
        <Route path="logout" element={<LogoutPage />} />
        {/* Signup is public and outside the login gate: it creates the account
            it would otherwise be asked to authenticate. */}
        <Route path="signup" element={<SignupPage />} />
        {/* Where the invitation email lands. Public too — it signs the invitee
            in (or creates their account) before showing what to accept, and it
            never asks them for a company of their own. */}
        <Route path="invitations" element={<InvitationPage />} />
        {/* Where a provider's consent screen comes back to. Public, and outside
            any tenant path: the popup only carries the code to the API, whose
            `state` already says which tenant asked — putting it behind the login
            gate would trade the code for a redirect. The tenant-scoped copy
            below stays for the redirect URIs already registered that way. */}
        <Route path="oauth/callback" element={<OAuthCallbackPage />} />
        <Route element={<Login />}>
          {/* No company landing page */}
          <Route path="start" element={<NoCompanyLanding />} />

          {/* Home redirect to preferred company */}
          <Route path="home" element={<BackofficeLayout />}>
            <Route path="" element={<HomeRedirect />} />
          </Route>

          {/* Account and Companies management (no company context) */}
          <Route path="preferences" element={<BackofficeLayout />}>
            <Route path="" element={<PreferencesPage />} />
          </Route>
          <Route path="account" element={<BackofficeLayout />}>
            <Route path="" element={<ProfilePage />} />
          </Route>
          <Route path="companies" element={<BackofficeLayout />}>
            <Route path="" element={<CompaniesPage />} />
          </Route>

          {/* Onboarding runs full-screen: no sidebar, no distractions. */}
          <Route path=":company/onboarding" element={<OnboardingPage />} />

          {/* Company-scoped routes */}
          <Route path=":company" element={<BackofficeLayout />}>
            <Route path="home" element={<AdminHomePage />} />
            <Route path="calls" element={<CallsPage />} />
            {/* The conversation opens inside the list, so both routes are
                the same page — the session id is just its selection. */}
            <Route path="calls/session/:sessionId" element={<CallsPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="contacts/:contactId" element={<ContactsViewPage />} />
            <Route
              path="contacts/:contactId/edit"
              element={<ContactsEditPage />}
            />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="qualifications" element={<QualificationsPage />} />
            {/* As the call history: the list and the submission are one page,
                the id is its selection. */}
            <Route
              path="qualifications/:qualificationId"
              element={<QualificationsPage />}
            />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/:agentId" element={<ViewAgentPage />} />
            <Route path="agents/:agentId/edit" element={<EditAgentPage />} />
            <Route path="data" element={<DocumentsPage />} />
            <Route path="faq" element={<FaqPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/:messageId" element={<MessagesPage />} />
            <Route path="data/ask" element={<AskRepositoryPage />} />
            <Route
              path="data/metadata-config"
              element={<MetadataConfigPage />}
            />
            <Route path="settings/tools" element={<ToolsLayout />}>
              <Route index element={<ToolsPage />} />
              <Route path=":id" element={<ConfigureToolPage />} />
              <Route path="agents/:agentId" element={<AgentToolRedirect />} />
              <Route
                path="agents/:agentId/:toolId"
                element={<AgentToolRedirect />}
              />
            </Route>
            {/* "Entreprise": one entry in the main sidebar, its pages
                unfolding under it while one of them is open
                (company-settings/nav.tsx). */}
            <Route path="settings/general" element={<CompanySettingsPage />} />
            <Route path="settings/users" element={<UsersPage />} />
            <Route
              path="settings/notifications"
              element={<NotificationsSettingsPage />}
            />
            <Route path="settings/accounts" element={<AccountsListPage />} />
            <Route path="settings/api" element={<ApiPage />} />
            <Route path="settings/schedules" element={<SchedulesListPage />} />
            <Route path="settings/services" element={<ServicesListPage />} />
            <Route path="settings/members" element={<MembersPage />} />
            {/* The page was called "Accès" until it was named after what it
                lists; keep the old link working for anything bookmarked. */}
            <Route
              path="settings/accesses"
              element={<Navigate to="../settings/users" replace />}
            />
            <Route path="settings/connectors" element={<ConnectorsPage />} />
            <Route
              path="settings/connectors/personalization"
              element={<ConnectorsPersonalizationPage />}
            />
            <Route
              path="settings/connectors/number-requests/:requestId"
              element={<NumberRequestPage />}
            />
            <Route path="settings/voip-accounts" element={<DevicesPage />} />
            <Route
              path="settings/qualification-forms"
              element={<QualificationFormsPage />}
            />
            <Route
              path="settings/qualification-forms/create"
              element={<CreateQualificationFormPage />}
            />
            <Route
              path="settings/qualification-forms/:formId/edit"
              element={<EditQualificationFormPage />}
            />
            <Route path="oauth/callback" element={<OAuthCallbackPage />} />
            <Route path="*" element={<Navigate to="home" />} />
            <Route path="" element={<Navigate to="home" />} />

            {/* The back-office used to live under the tenant path. Those
                links (bookmarks, Sentry, e-mails) still work: they land on the
                same page of the standalone app. */}
            <Route path="admin/*" element={<LegacyAdminRedirect />} />
          </Route>
          {/* The platform back-office: its own application, outside any
              tenant path, because everything in it is cross-tenant. Built only
              for staff; `AdminLayout` re-checks the role on every render and
              the server gates every route with checkRole("SYSADMIN") — or
              checkStaffRead() for the few reads the read-only account (sales)
              is given. What that account is not given sits under `AdminOnly`. */}
          {backOffice && (
            <Route path="admin" element={<AdminLayout />}>
              {/* Clients — the subject the back-office is organised around. */}
              <Route path="clients" element={<AdminClientsPage />} />
              {/*
                A client's file. The tabs mount the *client's own* screens
                (`views/backoffice/devices/**`, its dashboard, its calls) rather
                than admin copies of them — the shell points them at this client
                and at the staff mount of the API (`ClientScopeProvider`), so the
                two screens cannot drift apart. "Données & historique" is the
                exception and deliberately so: it shows the rows themselves,
                which the client has no screen for.
              */}
              <Route path="clients/:clientId" element={<AdminClientPage />}>
                <Route path="overview" element={<ClientOverviewTab />} />
                <Route path="dashboard" element={<ClientDashboardTab />} />
                <Route path="calls" element={<ClientCallsTab />} />
                <Route
                  path="calls/session/:sessionId"
                  element={<ClientCallsTab />}
                />
                <Route element={<AdminOnly />}>
                  <Route path="voip" element={<DevicesTable />} />
                  <Route path="bases" element={<DeviceBasesTable />} />
                  <Route path="users" element={<AdminClientUsersTab />} />
                  <Route path="channels" element={<ClientChannelsTab />} />
                  <Route path="data" element={<ClientDataTab />} />
                </Route>
                <Route path="" element={<Navigate to="overview" replace />} />
              </Route>
              {/* The list moved and was renamed; the old paths still answer. */}
              <Route
                path="tenants"
                element={<Navigate to="/admin/clients" replace />}
              />
              <Route
                path="tenants/:clientId"
                element={<LegacyTenantRedirect />}
              />

              {/* Activité */}
              <Route path="live" element={<AdminControlCenterPage />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />

              <Route element={<AdminOnly />}>
                <Route
                  path="number-requests"
                  element={<AdminNumberRequestsPage />}
                />
                {/* The tenant is in the path because platform staff reach a
                    request from the cross-tenant queue, not from inside the
                    tenant — which is a place they cannot go. */}
                <Route
                  path="number-requests/:clientId/:requestId"
                  element={<AdminNumberRequestPage />}
                />
                <Route path="demo-spaces" element={<AdminDemoSpacesPage />} />
                <Route
                  path="provisioning"
                  element={<AdminProvisioningPage />}
                />
                <Route path="telephony" element={<AdminTelephonyPage />} />

                <Route path="sessions" element={<AdminSessionsPage />} />

                {/* Qualité de service */}
                <Route path="technical" element={<AdminTechnicalPage />} />
                <Route path="costs" element={<AdminCostsPage />} />
                <Route path="stability" element={<AdminStabilityPage />} />
                <Route
                  path="synthetic-calls"
                  element={<AdminSyntheticCallsPage />}
                />
                <Route path="media-health" element={<AdminMediaHealthPage />} />
                <Route
                  path="external-calls"
                  element={<AdminExternalCallsPage />}
                />
                <Route path="tool-errors" element={<AdminToolErrorsPage />} />

                {/* Plateforme */}
                <Route
                  path="context-pressure"
                  element={<AdminContextPressurePage />}
                />
                <Route path="credits" element={<AdminCreditsPage />} />

                {/* The generic explorer: one route for every table. */}
                <Route path="explorer" element={<AdminExplorerIndexPage />} />
                <Route
                  path="explorer/:table"
                  element={<AdminEntityTablePage />}
                />
                <Route
                  path="explorer/:table/:id"
                  element={<AdminEntityDetailPage />}
                />
              </Route>

              {/* The control centre is the way in: it is the page meant to
                  be left open, and every other subject is one click away. */}
              <Route path="" element={<Navigate to="live" replace />} />
              <Route path="*" element={<Navigate to="/admin/live" replace />} />
            </Route>
          )}
        </Route>
        <Route
          path="c/:sessionPublicId"
          element={<PublicChat />}
          errorElement={<RouteErrorPage publicChat />}
        />
        <Route path="*" element={<Navigate to="/start" />} />
        <Route path="" element={<Navigate to="/start" />} />
      </Route>,
    ),
  );

function ConfigGateway() {
  useTitle();
  useTheme();
  return <App />;
}

function App() {
  useTheme();
  useLanguage();
  const { data: user } = useUser();

  useEffect(() => {
    if (user) identifyUser(user);
  }, [user?.id]);

  const backOffice = backOfficeAccess(user?.role);
  const router = useMemo(() => routerGen(backOffice), [backOffice]);
  const mobile = useIsMobile();

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
      <WebSocketGateway />
      <Toaster richColors position={mobile ? "top-center" : "bottom-right"} />
    </ErrorBoundary>
  );
}

export default ConfigGateway;
